import {ReactElement, Suspense, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import useCurrentUserRoles from "../../hooks/useCurrentUserRoles";
import {loadingSpin, RoutingPaths} from "./routingContainer";
import {useAuth0} from "@auth0/auth0-react";
import {getCurrentUser, setCurrentUser} from "../../redux/applicationUserSlice";
import {useDispatch, useSelector} from "react-redux";
import {message} from "antd";
import {jwtDecode} from "jwt-decode";
import {useServerStatus} from "../../hooks/useServerStatus";
import {getApiServiceOrNull} from "../../services/serviceContainer";

export function AuthInitialized(props: { children: ReactElement, loadingIndicator: ReactElement }) {
    const auth = useAuth0();

    if (auth.isLoading) {
        return props.loadingIndicator || null;
    }

    return <Suspense fallback={loadingSpin}>
        {props.children}
    </Suspense>;
}

export function decodeAccessToken(token: string): Auth0AccessToken | null {
    try {
        return jwtDecode<Auth0AccessToken>(token);
    } catch (error) {
        console.error("Failed to decode token", error);
        return null;
    }
}

export function Protected(props: {
    children: ReactElement,
    loadingIndicator: ReactElement,
    allowedForRoles?: string[]
}) {
    const auth = useAuth0();
    const navigate = useNavigate();
    const roles = useCurrentUserRoles();
    const currentUser = useSelector(getCurrentUser);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!roles) {
            return;
        }

        if (props.allowedForRoles && !roles.some(r => props.allowedForRoles!.includes(r))) {
            message.warning("Wygląda na to, że nie masz dostępu do tej strony.")
            navigate(RoutingPaths.mainPage.route, {replace: true});
        }
    }, [roles, props.allowedForRoles, navigate]);

    const isOnline = useServerStatus(5000);

    useEffect(() => {
        if (isOnline || currentUser || !auth.isAuthenticated) {
            return;
        }

        const cachedUser = localStorage.getItem("currentUser");
        if (cachedUser) {
            dispatch(setCurrentUser(JSON.parse(cachedUser)));
        }
    }, [auth.isAuthenticated, currentUser, dispatch, isOnline]);

    if (auth.isLoading) {
        return props.loadingIndicator;
    }

    if (!auth.isAuthenticated) {
        if (isOnline) {
            auth.loginWithRedirect({
                appState: {
                    returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`
                }
            });
        }

        return props.loadingIndicator;
    }

    if (!currentUser) {
        return props.loadingIndicator;
    }

    if (!getApiServiceOrNull()) {
        return props.loadingIndicator;
    }

    if (!isOnline) {
        return <Suspense fallback={loadingSpin}>
            {props.children}
        </Suspense>;
    }

    return <Suspense fallback={loadingSpin}>
        {props.children}
    </Suspense>;
}

export interface Auth0AccessToken {
    iss: string;
    sub: string;
    aud: string[];
    iat: number;
    exp: number;
    scope?: string;
    azp?: string;
    permissions?: string[];
}
